import Button, {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  getButtonColor,
  getButtonIconAlignment,
  getButtonSize,
  getButtonVariant,
} from '@components/buttons/button'
import ButtonLink from '@components/buttons/button-link'
import { type SanityLink } from '@data/sanity/queries/types/link'
import { useLogoutUser } from '@lib/auth'
import { LanguageContext } from '@lib/language-context'
import {
  type PageType,
  type Slugs,
  getFormattedQueryString,
  getLinkPageUrl,
  isRouteCollection,
} from '@lib/routes'
import { SiteContext } from '@lib/site-context'
import cx from 'classnames'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import {
  type HTMLAttributes,
  type InputEvent,
  type MouseEvent,
  type ReactNode,
  useContext,
} from 'react'

interface LinkProps {
  link: SanityLink
  onClick?: () => void
  children?: ReactNode
  showCollectionCount?: boolean
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  buttonColor?: ButtonColor
  nextLinkClassName?: string
}

type ExternalLinkProps = Pick<SanityLink, 'url' | 'buttonStyle' | 'isButton'> &
  Pick<
    LinkProps,
    'buttonVariant' | 'buttonSize' | 'buttonColor' | 'children'
  > & {
    id?: string
    ariaLabel?: string
    tabIndex?: number
    className?: string
  }

const ExternalLink = ({
  id,
  url,
  ariaLabel,
  tabIndex,
  buttonVariant,
  buttonSize,
  buttonColor,
  buttonStyle,
  isButton,
  className,
  children,
}: ExternalLinkProps) => {
  const buttonStyles = isButton ? buttonStyle : undefined

  return (
    <ButtonLink
      id={id}
      href={url}
      target="_blank"
      aria-label={ariaLabel}
      tabIndex={tabIndex}
      rel="noopener noreferrer"
      variant={buttonVariant ?? getButtonVariant(buttonStyles?.variant)}
      size={buttonSize ?? getButtonSize(buttonStyles?.size)}
      color={buttonColor ?? getButtonColor(buttonStyles?.color)}
      icon={buttonStyles?.icon}
      iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
      className={cx(
        {
          btn: isButton,
          'w-full': buttonStyles?.isFullWidth,
        },
        className,
      )}
    >
      {children}
    </ButtonLink>
  )
}

const Link = ({
  link,
  children,
  tabIndex,
  onClick,
  className,
  showCollectionCount,
  buttonVariant,
  buttonSize,
  buttonColor,
  'aria-label': ariaLabel,
  nextLinkClassName,
}: LinkProps & HTMLAttributes<HTMLAnchorElement>) => {
  const { getProductCount } = useContext(SiteContext)
  const { locale } = useContext(LanguageContext)
  const router = useRouter()
  const logoutUser = useLogoutUser()

  const pageType = link.page?._type
  const pageSlug = link.page?.slug?.current
  const isCollection = !!pageType && isRouteCollection(pageType)
  const collectionSlug = isCollection && pageSlug ? pageSlug : 'all'
  const collectionCount = getProductCount(collectionSlug)

  const { _id: id, url, isButton, buttonStyle } = link

  // External link
  if (url) {
    return (
      <ExternalLink
        id={id}
        url={url}
        ariaLabel={ariaLabel}
        tabIndex={tabIndex}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        buttonColor={buttonColor}
        buttonStyle={buttonStyle}
        isButton={isButton}
        className={className}
      >
        {children}
      </ExternalLink>
    )
  }

  // Internal Page
  if (!pageType) {
    return null
  }

  let resolvedPageType: PageType = pageType
  let resolvedSlugs: Slugs = pageSlug ?? null

  if (pageType === 'collection' && link.collectionLinkContext) {
    if (link.collectionLinkContext === 'shopAll') {
      resolvedPageType = 'shopPageChild'
    }

    if (link.collectionLinkContext === 'sale') {
      resolvedPageType = 'shopSalePageChild'
    }

    if (
      link.collectionLinkContext === 'parent' &&
      link.parentCollection?.slug?.current
    ) {
      resolvedPageType = 'collectionChild'
      resolvedSlugs = [link.parentCollection.slug.current, pageSlug ?? '']
    }
  }

  const pageUrl = `${getLinkPageUrl(
    resolvedPageType,
    resolvedSlugs,
  )}${getFormattedQueryString(link.queryString)}`

  const getClickHandler = (event: MouseEvent<HTMLButtonElement>) => {
    if (pageType === 'logoutPage') {
      event.preventDefault()

      logoutUser(() => {
        const url = getLinkPageUrl('loginPage')
        router.push(url, url, {
          locale,
        })
      })
    }

    if (onClick) {
      onClick()
    }
  }

  const beforeInputHandler = (event: InputEvent<HTMLButtonElement>) => {
    if (pageType === 'logoutPage') {
      event.preventDefault()

      logoutUser(() => {
        const url = getLinkPageUrl('loginPage')
        router.push(url, url, {
          locale,
        })
      })
    }

    if (onClick) {
      onClick()
    }
  }

  const buttonStyles = isButton ? buttonStyle : undefined

  return (
    <NextLink href={pageUrl} className={nextLinkClassName}>
      <Button
        id={id}
        role="link"
        tabIndex={tabIndex}
        onClick={getClickHandler}
        onBeforeInput={beforeInputHandler}
        aria-label={ariaLabel}
        variant={buttonVariant ?? getButtonVariant(buttonStyles?.variant)}
        size={buttonSize ?? getButtonSize(buttonStyles?.size)}
        color={buttonColor ?? getButtonColor(buttonStyles?.color)}
        icon={buttonStyles?.icon}
        iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
        className={cx(
          {
            btn: isButton,
            'w-full': buttonStyles?.isFullWidth,
          },
          className,
        )}
      >
        {children}

        {!!showCollectionCount && isCollection && (
          <span
            aria-hidden="true"
            className="inline-block relative ml-2 leading-none align-super text-[.5em]"
          >
            {collectionCount}
          </span>
        )}
      </Button>
    </NextLink>
  )
}

export default Link
